import { useEffect, useState } from "react";
import { Eye, EyeOff, LockKeyhole, LogIn, User } from "lucide-react";
import logo from "../assets/logo-internacional.jpg";
import AppFooter from "../components/AppFooter";
import { useAuth } from "../context/AuthContext";

const REMEMBERED_LOGIN_KEY = "lainternacional_remembered_login";
const DEFAULT_LOGIN = { usuario: "", password: "" };

const getRememberedLogin = () => {
  try {
    const savedLogin = localStorage.getItem(REMEMBERED_LOGIN_KEY);
    return savedLogin ? { ...DEFAULT_LOGIN, usuario: JSON.parse(savedLogin).usuario || "" } : DEFAULT_LOGIN;
  } catch {
    return DEFAULT_LOGIN;
  }
};

export default function Login() {
  const { login, verifyWhatsapp } = useAuth();
  const [form, setForm] = useState(getRememberedLogin);
  const [rememberUser, setRememberUser] = useState(() =>
    Boolean(localStorage.getItem(REMEMBERED_LOGIN_KEY)),
  );
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [challenge, setChallenge] = useState(null);
  const [code, setCode] = useState("");
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    if (!challenge) return undefined;
    const timer = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(timer);
  }, [challenge]);
  const resendSeconds = challenge ? Math.max(0, Math.ceil((challenge.resendAt - now) / 1000)) : 0;
  const expired = challenge && now >= challenge.expiresAt;

  const solicitarCodigo = async () => {
    const data = await login(form);
    if (data.authenticated) return data;
    const sentAt = Date.now();
    setNow(sentAt);
    setCode("");
    setChallenge({ ...data, expiresAt: sentAt + data.expiresIn * 1000, resendAt: sentAt + data.resendAfter * 1000 });
    return data;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError("");

    try {
      setLoading(true);
      if (!challenge) {
        const data = await solicitarCodigo();
        if (!data.authenticated) return;
      } else {
        await verifyWhatsapp({ challengeId: challenge.challengeId, code });
      }

      if (rememberUser) {
        localStorage.setItem(
          REMEMBERED_LOGIN_KEY,
          JSON.stringify({ usuario: form.usuario }),
        );
      } else {
        localStorage.removeItem(REMEMBERED_LOGIN_KEY);
      }
    } catch (err) {
      if (err.response?.data?.restartLogin) {
        setChallenge(null);
        setCode("");
        setForm((prev) => ({ ...prev, password: "" }));
      }
      setError(err.response?.data?.error || "No se pudo iniciar sesión.");
    } finally {
      setLoading(false);
    }
  };

  const reenviarCodigo = async () => {
    setError("");
    setLoading(true);
    try { await solicitarCodigo(); }
    catch (err) { setError(err.response?.data?.error || "No se pudo reenviar el codigo."); }
    finally { setLoading(false); }
  };

  return (
    <div className="login-page">
      <section className="login-panel">
        <div className="login-logo-stage">
          <img src={logo} className="login-logo" alt="La Internacional" />
        </div>
        <h1>Gestor de costos</h1>

        {error && <div className="alert alert-error" role="alert">{error}</div>}

        <form className="login-card" onSubmit={handleSubmit}>
          <div className="field">
            <label htmlFor="login-user">Usuario</label>
            <div className="login-input">
              <User size={17} aria-hidden="true" />
              <input
                id="login-user"
                value={form.usuario}
                onChange={(event) =>
                  setForm((prev) => ({ ...prev, usuario: event.target.value }))
                }
                autoComplete="username"
                required
                disabled={loading || Boolean(challenge)}
              />
            </div>
          </div>

          <div className="field">
            <label htmlFor="login-password">Contraseña</label>
            <div className="login-input has-action">
              <LockKeyhole size={17} aria-hidden="true" />
              <input
                id="login-password"
                type={showPassword ? "text" : "password"}
                value={form.password}
                onChange={(event) =>
                  setForm((prev) => ({ ...prev, password: event.target.value }))
                }
                autoComplete="current-password"
                required
                disabled={loading || Boolean(challenge)}
              />
              <button
                className="password-toggle"
                type="button"
                onClick={() => setShowPassword((prev) => !prev)}
                aria-label={showPassword ? "Ocultar contraseña" : "Ver contraseña"}
                title={showPassword ? "Ocultar contraseña" : "Ver contraseña"}
              >
                {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
              </button>
            </div>
          </div>

          {challenge && (
            <div className="field">
              <p role="status">
                Enviamos un codigo por WhatsApp al {challenge.phoneMasked}. Valido por 5 minutos.
              </p>
              <label htmlFor="login-whatsapp-code">Codigo de WhatsApp (6 digitos)</label>
              <div className="login-input">
                <LockKeyhole size={17} aria-hidden="true" />
                <input
                  id="login-whatsapp-code"
                  type="text"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  pattern="[0-9]{6}"
                  maxLength={6}
                  value={code}
                  onChange={(event) => setCode(event.target.value.replace(/\D/g, "").slice(0, 6))}
                  required
                  autoFocus
                  disabled={loading || expired}
                />
              </div>
              {expired && <p role="status">El codigo vencio. Solicite uno nuevo.</p>}
              <button className="btn" type="button" onClick={reenviarCodigo} disabled={loading || resendSeconds > 0}>
                {resendSeconds > 0 ? `Reenviar en ${resendSeconds}s` : "Reenviar codigo"}
              </button>
              <button className="btn" type="button" disabled={loading} onClick={() => {
                setChallenge(null); setCode(""); setError("");
                setForm((prev) => ({ ...prev, password: "" }));
              }}>Cambiar usuario</button>
            </div>
          )}

          <label className="remember-password">
            <input
              type="checkbox"
              checked={rememberUser}
              onChange={(event) => setRememberUser(event.target.checked)}
            />
            <span>Recordar usuario</span>
          </label>

          <button className="btn btn-primary" type="submit" disabled={loading || Boolean(expired) || (Boolean(challenge) && code.length !== 6)}>
            <LogIn size={18} />
            {loading ? (challenge ? "Verificando..." : "Enviando codigo...") : (challenge ? "Verificar e ingresar" : "Ingresar")}
          </button>
        </form>

        <AppFooter className="login-footer" />
      </section>

      <section className="login-visual" aria-hidden="true" />
    </div>
  );
}
